
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchBusinessById,
  getThemeConfigFile,
  updateBusiness,
  uploadAppBusinessAsset,
  uploadBusinessAsset,
  uploadBusinessTheme,
  uploadDashboardTheme
} from "../../store/actions/businessActions";

import { clearSuccessFlags, clearErrors, BusinessStatus } from "../../store/slices/businessSlice";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelectInput";
import CustomButton from "../../components/CustomButton";
import ActiveInActiveButton from "../clients/components/ActiveInActiveButton";
import useCustomStyles from "../../hooks/useCustomStyles";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import { selectState } from "../../store/slices/themeSlice";
import { fileToBase64, themeToJSObjectString } from "../../utils";
import { MinusCircle, PlusCircle } from "lucide-react";
import { on } from "events";
import { json } from "stream/consumers";

interface BusinessDetailsProps {
  businessId?: string;
}

interface UploadState {
  show: boolean;
  file: File | null;
  fileName: string;
  preview: string | null;
  uploading: boolean;
}

const BusinessDetails: React.FC<BusinessDetailsProps> = ({ businessId: propBusinessId }) => {
  const { id: paramBusinessId } = useParams<{ id: string }>();
  const businessId = propBusinessId || paramBusinessId;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { theme, businessThemeRaw } = useSelector(selectState);
  
  const {
    currentBusiness,
    loading,
    updating,
    updateSuccess,
    updateError,
    assetUploading,
    assetUploadSuccess,
    assetUploadError,
    dashboardThemeUploading, 
    dashboardThemeUploadSuccess, 
    dashboardThemeUploadError, 
    dashboardThemeVersion,
    selectedBusinessThemeLoading,
    selectedBusinessTheme, 
    uploadError,
    uploadSuccess,
    uploading,
  } = useSelector((state: RootState) => state.business_slice);
    console.log("Current Business:", currentBusiness);
   
  const classes = useCustomStyles(accessCodesStyles, theme);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if(uploadSuccess){
        toast.success("uploaded successfully!");
        dispatch(clearSuccessFlags());
       // dispatch(fetchBusinessById({id: businessId!}));
    }else if(uploadError){
        toast.error(uploadError);
        dispatch(clearErrors());
    }
  },[uploadSuccess, uploadError]);

  // States for uploading App assets (logos, backgrounds, onboarding)
  // App Assets Upload States
const [appLogoLight, setAppLogoLight] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "logo-light.svg",
    preview: null,
    uploading: false,
  });
  
  const [appLogoDark, setAppLogoDark] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "logo-dark.svg",
    preview: null,
    uploading: false,
  });
  
  const [appBackgroundLight, setAppBackgroundLight] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "background-light.jpg",
    preview: null,
    uploading: false,
  });
  
  const [appBackgroundDark, setAppBackgroundDark] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "background-dark.jpg",
    preview: null,
    uploading: false,
  });
  
  const [onboardingImages, setOnboardingImages] = useState<Array<{
    id: number;
    file: File | null;
    fileName: string;
    preview: string | null;
    uploading: boolean;
    show: boolean;
  }>>([]);
  
  const [nextOnboardingId, setNextOnboardingId] = useState(1);
  
  // Upload states for different asset types
  const [logoUpload, setLogoUpload] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "primary_logo_light.svg",
    preview: null,
    uploading: false,
  });

  const [backgroundUpload, setBackgroundUpload] = useState<UploadState>({
    show: false,
    file: null,
    fileName: "dashboard_background.jpg",
    preview: null,
    uploading: false,
  });

  const [themeUpload, setThemeUpload] = useState({
    show: false,
    json: "",
    uploading: false,
  });
  // useEffect(()=>{
  //   if(currentBusiness?.code)
  //   getThemeConfigFile(currentBusiness?.code).then((res)=>{
  //       if(res){
  //           console.log('theme config file content ', res)
  //           setThemeUpload(prev => ({
  //               ...prev,
  //               json: res.config ? `${themeToJSObjectString(res.config)}` : "",
  //           }));
  //       }
  //   });
  // },[currentBusiness?.code]);
  // console.log('theme app ', themeUpload)

  // 👇 NEW: Dashboard Theme Upload State (Firestore-based)
  const [dashboardThemeUpload, setDashboardThemeUpload] = useState({
    show: false,
    json: selectedBusinessTheme? `${themeToJSObjectString(selectedBusinessTheme)}` : businessThemeRaw? `${themeToJSObjectString(businessThemeRaw)}` : "",
    uploading: false,
  });

  useEffect(() => {
    setDashboardThemeUpload({
        show: false,
        json: selectedBusinessTheme? `${themeToJSObjectString(selectedBusinessTheme)}` : businessThemeRaw? `${themeToJSObjectString(businessThemeRaw)}` : "",
        uploading: false,
      })
  },[selectedBusinessTheme]);

  const [adminsList, setAdminsList] = useState<Array<{
    id: string;
    email: string;
    roles: string[];
    created_at: string | null;
    updated_at: string | null;
  }>>([]);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    display_name: Yup.string(),
    type: Yup.string().required("Type is required"),
    enable_advisors: Yup.boolean(),
    need_client_id: Yup.boolean(),
    need_admin_dashboard: Yup.boolean(),
    status: Yup.string().oneOf(["active", "inactive"]),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      display_name: "",
      type: "",
      enable_advisors: false,
      need_client_id: false,
      need_admin_dashboard: false,
      status: "active" as BusinessStatus,
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(updateBusiness({
        id: businessId!,
        updates: {...values}
      }));
      setIsEditing(false);
      formik.setTouched({});
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (businessId) {
      dispatch(fetchBusinessById({id: businessId}));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (currentBusiness) {
      formik.setValues({
        name: currentBusiness.name || "",
        display_name: currentBusiness.display_name || "",
        type: currentBusiness.type || "",
        enable_advisors: currentBusiness.enable_advisors || false,
        need_client_id: currentBusiness.need_client_id || false,
        need_admin_dashboard: currentBusiness.need_admin_dashboard || false,
        status: currentBusiness.status || "active" as BusinessStatus,
      });

      if (currentBusiness.admins) {
        setAdminsList(currentBusiness.admins);
      }
    }
  }, [currentBusiness]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("Business updated successfully!");
      dispatch(clearSuccessFlags());
      setIsEditing(false);
      dispatch(fetchBusinessById({id: businessId!}));
    }
  }, [updateSuccess, dispatch, businessId]);

  useEffect(() => {
    if (updateError) {
      toast.error(updateError);
      dispatch(clearErrors());
    }
  }, [updateError, dispatch]);

  // Handle asset upload success
  useEffect(() => {
    if (assetUploadSuccess) {
      toast.success("Asset uploaded successfully!");
      
      // Reset all upload states
      setLogoUpload({
        show: false,
        file: null,
        fileName: "primary_logo_light.svg",
        preview: null,
        uploading: false,
      });
      
      setBackgroundUpload({
        show: false,
        file: null,
        fileName: "dashboard_background.svg",
        preview: null,
        uploading: false,
      });
      
      setThemeUpload({
        show: false,
        json: "",
        uploading: false,
      });
      
      dispatch(clearSuccessFlags());
      dispatch(fetchBusinessById({id: businessId!}));
    }
  }, [assetUploadSuccess, dispatch, businessId]);

  // 👇 NEW: Handle dashboard theme upload success
  useEffect(() => {
    if (dashboardThemeUploadSuccess) {
      toast.success(`Dashboard Theme v${dashboardThemeVersion || ''} uploaded successfully!`);
      
      // Reset dashboard theme upload state
      setDashboardThemeUpload({
        show: false,
        json: "",
        uploading: false,
      });
      
      dispatch(clearSuccessFlags());
      dispatch(fetchBusinessById({id: businessId!}));
    }
  }, [dashboardThemeUploadSuccess, dashboardThemeVersion, dispatch, businessId]);

  useEffect(() => {
    if (assetUploadError) {
      toast.error(assetUploadError);
      dispatch(clearErrors());
    }
  }, [assetUploadError, dispatch]);

  // 👇 NEW: Handle dashboard theme upload error
  useEffect(() => {
    if (dashboardThemeUploadError) {
      toast.error(dashboardThemeUploadError);
      dispatch(clearErrors());
    }
  }, [dashboardThemeUploadError, dispatch]);

  const toggleEdit = () => {
    if (isEditing) {
      if (currentBusiness) {
        formik.setValues({
          name: currentBusiness.name || "",
          display_name: currentBusiness.display_name || "",
          type: currentBusiness.type || "",
          enable_advisors: currentBusiness.enable_advisors || false,
          need_client_id: currentBusiness.need_client_id || false,
          need_admin_dashboard: currentBusiness.need_admin_dashboard || false,
          status: currentBusiness.status || "active" as BusinessStatus,
        });
      }
    }
    setIsEditing(!isEditing);
  };

  // Handle file selection for logos
  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('svg') && !file.type.includes('png') && !file.type.includes('jpeg')) {
      toast.error("Please select an SVG, PNG, or JPEG file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoUpload(prev => ({
        ...prev,
        file,
        preview: e.target?.result as string,
      }));
    };
    reader.readAsDataURL(file);

    const ext = file.name.split('.').pop() || 'svg';
    const baseName = "primary_logo_light";
    setLogoUpload(prev => ({
      ...prev,
      file,
      fileName: `${baseName}.${ext}`,
    }));
  };

  // Handle file selection for backgrounds
  const handleBackgroundSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png') && !file.type.includes('jpeg') && !file.type.includes('svg')) {
      toast.error("Please select a JPG, JPEG, or PNG file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setBackgroundUpload(prev => ({
        ...prev,
        file,
        preview: e.target?.result as string,
      }));
    };
    reader.readAsDataURL(file);

    const ext = file.name.split('.').pop() || 'jpg';
    const baseName = "dashboard_background";
    setBackgroundUpload(prev => ({
      ...prev,
      file,
      fileName: `${baseName}.${ext}`,
    }));
  };

  // Handle logo upload
  const handleUploadLogo = async () => {
    if (!logoUpload.file) {
      toast.error("Please select a logo file");
      return;
    }

    if (!logoUpload.fileName || logoUpload.fileName.trim() === "") {
      toast.error("Please enter a filename");
      return;
    }

    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }

    let finalFileName = logoUpload.fileName;
    if (!finalFileName.includes('.')) {
      const ext = logoUpload.file.name.split('.').pop() || 'svg';
      finalFileName = `${finalFileName}.${ext}`;
    }

    setLogoUpload(prev => ({ ...prev, uploading: true }));
    try{
        const file64 = await fileToBase64(logoUpload.file);
        dispatch(uploadBusinessAsset({
            clientCode: currentBusiness.code,
            fileType: 'logo',
            fileName: finalFileName,
            fileData: file64,
        }));
    }catch(e){
        toast.error("Error uploading logo");
        setLogoUpload(prev => ({ ...prev, uploading: false }));
        return;
    }
  };

  // Handle background upload
  const handleUploadBackground = async () => {
    if (!backgroundUpload.file) {
      toast.error("Please select a background file");
      return;
    }

    if (!backgroundUpload.fileName || backgroundUpload.fileName.trim() === "") {
      toast.error("Please enter a filename");
      return;
    }

    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }

    let finalFileName = backgroundUpload.fileName;
    if (!finalFileName.includes('.')) {
      const ext = backgroundUpload.file.name.split('.').pop() || 'jpg';
      finalFileName = `${finalFileName}.${ext}`;
    }

    setBackgroundUpload(prev => ({ ...prev, uploading: true }));

    try{
        const file64 = await fileToBase64(backgroundUpload.file);
        dispatch(uploadBusinessAsset({
            clientCode: currentBusiness.code,
            fileType: 'background',
            fileName: finalFileName,
            fileData: file64,
        }));
    }catch(e){
        toast.error("Error uploading background");
        setBackgroundUpload(prev => ({ ...prev, uploading: false }));
        return;
    }
  };

  // Handle App theme upload (to external server)
  const handleUploadTheme = () => {
    if (!themeUpload.json || themeUpload.json.trim() === "") {
      toast.error("Please enter JSON theme data");
      return;
    }

    try {
      JSON.parse(themeUpload.json);
    } catch (error) {
      toast.error("Invalid JSON format");
      return;
    }

    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }

    setThemeUpload(prev => ({ ...prev, uploading: true }));

    const jsonString = themeUpload.json;
   // const base64Data = btoa(unescape(encodeURIComponent(jsonString)));
    
    dispatch(uploadBusinessTheme({
      clientCode: currentBusiness.code,
      jsonTheme: jsonString,
    }));
  };

 // 👇 Updated: Handle dashboard theme upload (accepts JS object, parses to JSON)
const handleUploadDashboardTheme = () => {
    if (!dashboardThemeUpload.json || dashboardThemeUpload.json.trim() === "") {
      toast.error("Please enter dashboard theme data");
      return;
    }
  
    // Validate and parse JavaScript object to JSON
    let themeObject;
    try {
      // Use eval to parse JavaScript object notation
      themeObject = eval('(' + dashboardThemeUpload.json + ')');
      
      // Validate it's an object
      if (typeof themeObject !== 'object' || themeObject === null) {
        toast.error("Theme data must be a valid object");
        return;
      }
    } catch (error) {
      console.error("Theme parse error:", error);
      toast.error(`Invalid JavaScript object format`);
      return;
    }
  
    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }
  
    setDashboardThemeUpload(prev => ({ ...prev, uploading: true }));
  
    dispatch(uploadDashboardTheme({
      clientCode: currentBusiness.code,
      theme: themeObject, // Send parsed object
    }));
  };


   //// APP Assets upload handlers (to external server)

   // Handle app logo selection
const handleAppLogoSelect = (
    file: File | null,
    type: 'light' | 'dark',
    setter: React.Dispatch<React.SetStateAction<UploadState>>
  ) => {
    if (!file) return;
  
    if (!file.type.includes('svg') && !file.type.includes('png') && !file.type.includes('jpeg')) {
      toast.error("Please select an SVG, PNG, or JPEG file");
      return;
    }
  
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }
  
    const reader = new FileReader();
    reader.onload = (e) => {
      setter(prev => ({
        ...prev,
        file,
        preview: e.target?.result as string,
      }));
    };
    reader.readAsDataURL(file);
  
    const ext = file.name.split('.').pop() || 'svg';
    setter(prev => ({
      ...prev,
      file,
      fileName: `logo-${type}.${ext}`,
    }));
  };
  
  // Handle app background selection
  const handleAppBackgroundSelect = (
    file: File | null,
    type: 'light' | 'dark',
    setter: React.Dispatch<React.SetStateAction<UploadState>>
  ) => {
    if (!file) return;
  
    if (!file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png')) {
      toast.error("Please select a JPG, JPEG, or PNG file");
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }
  
    const reader = new FileReader();
    reader.onload = (e) => {
      setter(prev => ({
        ...prev,
        file,
        preview: e.target?.result as string,
      }));
    };
    reader.readAsDataURL(file);
  
    const ext = file.name.split('.').pop() || 'jpg';
    setter(prev => ({
      ...prev,
      file,
      fileName: `background-${type}.${ext}`,
    }));
  };
  
  // Handle onboarding image selection
  const handleOnboardingSelect = (file: File | null, index: number) => {
    if (!file) return;
  
    if (!file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png')) {
      toast.error("Please select a JPG, JPEG, or PNG file");
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }
  
    const reader = new FileReader();
    reader.onload = (e) => {
      setOnboardingImages(prev => 
        prev.map((item, i) => 
          i === index 
            ? { 
                ...item, 
                file, 
                preview: e.target?.result as string,
                fileName: `onboarding-${item.id}.${file.name.split('.').pop() || 'jpg'}`
              }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };
  
  // Add new onboarding image input
  const addOnboardingImage = () => {
    setOnboardingImages(prev => [
      ...prev,
      {
        id: nextOnboardingId,
        file: null,
        fileName: `onboarding-${nextOnboardingId}.jpg`,
        preview: null,
        uploading: false,
        show: true,
      }
    ]);
    setNextOnboardingId(prev => prev + 1);
  };
  
  // Remove onboarding image input
  const removeOnboardingImage = (index: number) => {
    setOnboardingImages(prev => prev.filter((_, i) => i !== index));
  };
  
//   // Handle app logo upload
//   const handleUploadAppLogo = async (type: 'light' | 'dark', state: UploadState, setter: React.Dispatch<React.SetStateAction<UploadState>>) => {
//     if (!state.file) {
//       toast.error(`Please select a ${type} logo file`);
//       return;
//     }
  
//     if (!currentBusiness?.code) {
//       toast.error("Business code not found");
//       return;
//     }
  
//     setter(prev => ({ ...prev, uploading: true }));
//     try {
//       const file64 = await fileToBase64(state.file);
//       dispatch(uploadAppBusinessAsset({
//         clientCode: currentBusiness.code,
//         fileType: 'logos',
//         fileName: state.fileName,
//         fileData: file64,
//       }));
//     } catch (e) {
//       toast.error(`Error uploading ${type} logo`);
//       setter(prev => ({ ...prev, uploading: false }));
//     }
//   };
  
//   // Handle app background upload
//   const handleUploadAppBackground = async (type: 'light' | 'dark', state: UploadState, setter: React.Dispatch<React.SetStateAction<UploadState>>) => {
//     if (!state.file) {
//       toast.error(`Please select a ${type} background file`);
//       return;
//     }
  
//     if (!currentBusiness?.code) {
//       toast.error("Business code not found");
//       return;
//     }
  
//     setter(prev => ({ ...prev, uploading: true }));
//     try {
//       const file64 = await fileToBase64(state.file);
//       dispatch(uploadAppBusinessAsset({
//         clientCode: currentBusiness.code,
//         fileType: 'backgrounds',
//         fileName: state.fileName,
//         fileData: file64,
//       }));
//     } catch (e) {
//       toast.error(`Error uploading ${type} background`);
//       setter(prev => ({ ...prev, uploading: false }));
//     }
//   };
  
//   // Handle onboarding image upload
//   const handleUploadOnboarding = async (index: number, item: typeof onboardingImages[0]) => {
//     if (!item.file) {
//       toast.error("Please select an onboarding image");
//       return;
//     }
  
//     if (!currentBusiness?.code) {
//       toast.error("Business code not found");
//       return;
//     }
  
//     setOnboardingImages(prev => 
//       prev.map((img, i) => 
//         i === index ? { ...img, uploading: true } : img
//       )
//     );
  
//     try {
//       const file64 = await fileToBase64(item.file);
//       dispatch(uploadAppBusinessAsset({
//         clientCode: currentBusiness.code,
//         fileType: 'onboarding',
//         fileName: item.fileName,
//         fileData: file64,
//       }));
//     } catch (e) {
//       toast.error("Error uploading onboarding image");
//       setOnboardingImages(prev => 
//         prev.map((img, i) => 
//           i === index ? { ...img, uploading: false } : img
//         )
//       );
//     }
//   };


const {
    appAssetUploading,
    appAssetUploadSuccess,
    appAssetUploadError,
  } = useSelector((state: RootState) => state.business_slice);
  
  // Add useEffect for app asset upload success
  useEffect(() => {
    if (appAssetUploadSuccess) {
      toast.success("App asset uploaded successfully!");
      
      // Reset all app asset upload states
      setAppLogoLight({ show: false, file: null, fileName: "logo-light.svg", preview: null, uploading: false });
      setAppLogoDark({ show: false, file: null, fileName: "logo-dark.svg", preview: null, uploading: false });
      setAppBackgroundLight({ show: false, file: null, fileName: "background-light.jpg", preview: null, uploading: false });
      setAppBackgroundDark({ show: false, file: null, fileName: "background-dark.jpg", preview: null, uploading: false });
      setOnboardingImages([]);
      setNextOnboardingId(1);
      
      dispatch(clearSuccessFlags());
      dispatch(fetchBusinessById({ id: businessId! }));
    }
  }, [appAssetUploadSuccess, dispatch, businessId]);
  
  // Add useEffect for app asset upload error
  useEffect(() => {
    if (appAssetUploadError) {
      toast.error(appAssetUploadError);
      dispatch(clearErrors());
    }
  }, [appAssetUploadError, dispatch]);
  
  // Handle app logo upload
  const handleUploadAppLogo = async (type: 'light' | 'dark', state: UploadState, setter: React.Dispatch<React.SetStateAction<UploadState>>) => {
    if (!state.file) {
      toast.error(`Please select a ${type} logo file`);
      return;
    }
  
    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }
  
    setter(prev => ({ ...prev, uploading: true }));
    
    try {
      const file64 = await fileToBase64(state.file);
      const resultAction = await dispatch(uploadAppBusinessAsset({
        clientCode: currentBusiness.code,
        fileType: 'logos',
        fileName: state.fileName,
        fileData: file64,
      }));
      
      // Check if the action was rejected
      if (uploadAppBusinessAsset.rejected.match(resultAction)) {
        const errorMessage = resultAction.payload as string || `Error uploading ${type} logo`;
        toast.error(errorMessage);
        setter(prev => ({ ...prev, uploading: false }));
      }
      // Success will be handled by the useEffect above
    } catch (e) {
      console.error(`Error uploading ${type} logo:`, e);
      toast.error(`Error uploading ${type} logo`);
      setter(prev => ({ ...prev, uploading: false }));
    }
  };
  
  // Handle app background upload
  const handleUploadAppBackground = async (type: 'light' | 'dark', state: UploadState, setter: React.Dispatch<React.SetStateAction<UploadState>>) => {
    if (!state.file) {
      toast.error(`Please select a ${type} background file`);
      return;
    }
  
    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }
  
    setter(prev => ({ ...prev, uploading: true }));
    
    try {
      const file64 = await fileToBase64(state.file);
      const resultAction = await dispatch(uploadAppBusinessAsset({
        clientCode: currentBusiness.code,
        fileType: 'backgrounds',
        fileName: state.fileName,
        fileData: file64,
      }));
      
      // Check if the action was rejected
      if (uploadAppBusinessAsset.rejected.match(resultAction)) {
        const errorMessage = resultAction.payload as string || `Error uploading ${type} background`;
        toast.error(errorMessage);
        setter(prev => ({ ...prev, uploading: false }));
      }
      // Success will be handled by the useEffect above
    } catch (e) {
      console.error(`Error uploading ${type} background:`, e);
      toast.error(`Error uploading ${type} background`);
      setter(prev => ({ ...prev, uploading: false }));
    }
  };
  
  // Handle onboarding image upload
  const handleUploadOnboarding = async (index: number, item: typeof onboardingImages[0]) => {
    if (!item.file) {
      toast.error("Please select an onboarding image");
      return;
    }
  
    if (!currentBusiness?.code) {
      toast.error("Business code not found");
      return;
    }
  
    setOnboardingImages(prev => 
      prev.map((img, i) => 
        i === index ? { ...img, uploading: true } : img
      )
    );
  
    try {
      const file64 = await fileToBase64(item.file);
      const resultAction = await dispatch(uploadAppBusinessAsset({
        clientCode: currentBusiness.code,
        fileType: 'onboarding',
        fileName: item.fileName,
        fileData: file64,
      }));
      
      // Check if the action was rejected
      if (uploadAppBusinessAsset.rejected.match(resultAction)) {
        const errorMessage = resultAction.payload as string || "Error uploading onboarding image";
        toast.error(errorMessage);
        setOnboardingImages(prev => 
          prev.map((img, i) => 
            i === index ? { ...img, uploading: false } : img
          )
        );
      }
      // Success will be handled by the useEffect above
    } catch (e) {
      console.error("Error uploading onboarding image:", e);
      toast.error("Error uploading onboarding image");
      setOnboardingImages(prev => 
        prev.map((img, i) => 
          i === index ? { ...img, uploading: false } : img
        )
      );
    }
  };


  const isPartner = formik.values.type.toLowerCase() === "partner" || formik.values.type.toLowerCase() === "owner";

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const styles: Record<string, React.CSSProperties> = {
    // ... (keep all your existing styles)
    container: {
      padding: "20px",
      maxWidth: "800px",
      margin: "0 auto",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "30px",
    },
    backButton: {
      background: "none",
      border: "none",
      color: theme?.colorSchema?.colors?.primary || "#2196F3",
      cursor: "pointer",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "5px",
    },
    titleSection: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
      width: '40%',
    },
    statusBadge: {
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
      textTransform: "uppercase",
    },
    badge: {
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: "500",
      textTransform: "uppercase",
      marginLeft: "8px",
    },
    infoCard: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      marginBottom: "20px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      marginBottom: "16px",
      color: "#333",
      borderBottom: "1px solid #eee",
      paddingBottom: "8px",
    },
    fieldRow: {
      display: "flex",
      margin: "10px",
      alignItems: "center",
    },
    fieldLabel: {
      width: "200px",
      fontWeight: "500",
      color: "#666",
      fontSize: "14px",
    },
    fieldValue: {
      flex: 1,
      color: "#333",
      fontSize: "14px",
    },
    readOnlyValue: {
      padding: "8px 0",
    },
    timestampSection: {
      backgroundColor: "#f9f9f9",
      padding: "16px",
      borderRadius: "8px",
      marginTop: "20px",
    },
    timestampRow: {
      display: "flex",
      marginBottom: "8px",
      fontSize: "13px",
      color: "#666",
    },
    timestampLabel: {
      width: "120px",
      fontWeight: "500",
    },
    adminsSection: {
      marginTop: "20px",
    },
    adminCard: {
      backgroundColor: "#f5f5f5",
      padding: "12px",
      borderRadius: "8px",
      marginBottom: "8px",
    },
    adminEmail: {
      fontWeight: "500",
      marginBottom: "4px",
    },
    adminRoles: {
      fontSize: "12px",
      color: "#666",
    },
    buttonContainer: {
      display: "flex",
      gap: "10px",
      marginTop: "20px",
    },
    cancelButton: {
      backgroundColor: "#f5f5f5",
      color: "#333",
      border: "1px solid #ddd",
    },
    codeDisplay: {
      backgroundColor: "#f5f5f5",
      padding: "8px 12px",
      borderRadius: "6px",
      fontFamily: "monospace",
      fontSize: "14px",
      marginTop: "4px",
    },
    uploadSection: {
      marginTop: "20px",
      padding: "16px",
      backgroundColor: "#f9f9f9",
      borderRadius: "8px",
    },
    textarea: {
      width: "100%",
      minHeight: "150px",
      padding: "12px",
      borderRadius: "6px",
      border: "1px solid #ddd",
      fontFamily: "monospace",
      fontSize: "14px",
      resize: "vertical",
      marginBottom: "10px",
    },
    uploadButtonContainer: {
      display: "flex",
      gap: "10px",
      marginTop: "10px",
    },
    fileInput: {
      marginBottom: "10px",
    },
    fileNameInput: {
      width: "100%",
      padding: "10px",
      borderRadius: "6px",
      border: "1px solid #ddd",
      fontSize: "14px",
      marginBottom: "10px",
    },
    previewContainer: {
      maxWidth: "200px",
      maxHeight: "100px",
      marginTop: "10px",
      padding: "10px",
      border: "1px solid #eee",
      borderRadius: "4px",
      backgroundColor: "#f5f5f5",
    },
    assetUrl: {
      marginTop: "8px",
      fontSize: "12px",
      color: "#2196F3",
      wordBreak: "break-all",
    },
    badgeContainer: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap" as const,
      gap: "8px",
    },
    versionBadge: {
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "10px",
      fontWeight: "500",
      backgroundColor: "#e3f2fd",
      color: "#0d47a1",
      marginLeft: "8px",
    },
  };

  if (loading && !currentBusiness) {
    return (
      <div className={classes.container}>
        <div className={classes.tableContainer}>
          <p>Loading business details...</p>
        </div>
      </div>
    );
  }

  if (!currentBusiness) {
    return (
      <div className={classes.container}>
        <div className={classes.tableContainer}>
          <p>Business not found</p>
        </div>
      </div>
    );
  }


  // Helper function to get file status display
const getFileStatusDisplay = (uploaded: boolean, url?: string, filename?: string) => {
  if (uploaded && url) {
    return (
      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
        <span style={{ color: '#2e7d32', fontWeight: '500', marginRight: '8px' }}>✓ Uploaded</span>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#2196F3', fontSize: '12px', wordBreak: 'break-all' }}
        >
          {filename || 'View File'}
        </a>
      </div>
    );
  }
  return (
    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
      <span style={{ color: '#f57c00', fontWeight: '500' }}>✗ Not uploaded</span>
    </div>
  );
};

// useEffect(() => {
//     console.log("Current Business Data:", currentBusiness);
// },[currentBusiness]);

  return (
    <div className={classes.container}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <h2 style={{ margin: 0 }}>Business Details</h2>
            {/* {isPartner && (
              <div style={styles.badgeContainer}>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: currentBusiness.theme_uploaded ? "#e8f5e9" : "#fff3e0",
                    color: currentBusiness.theme_uploaded ? "#2e7d32" : "#f57c00",
                  }}
                >
                  {currentBusiness.theme_uploaded ? "✓ Theme" : "✗ Theme"}
                </span>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: currentBusiness.logo_uploaded ? "#e8f5e9" : "#fff3e0",
                    color: currentBusiness.logo_uploaded ? "#2e7d32" : "#f57c00",
                  }}
                >
                  {currentBusiness.logo_uploaded ? "✓ Logo" : "✗ Logo"}
                </span>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: currentBusiness.background_uploaded ? "#e8f5e9" : "#fff3e0",
                    color: currentBusiness.background_uploaded ? "#2e7d32" : "#f57c00",
                  }}
                >
                  {currentBusiness.background_uploaded ? "✓ BG" : "✗ BG"}
                </span>
              </div>
            )} */}
          </div>
          <CustomButton
            title={isEditing ? "Cancel Editing Mode" : "Enable Edit Business Mode"}
            name="toggleEdit"
            handleClick={toggleEdit}
            variant={isEditing ? "secondary" : "primary"}
            loading={updating}
          />
        </div>

        {/* Theme Upload Section (Legacy) - Only for partners */}
        {isPartner && isEditing && (
            <>
          <div style={styles.infoCard}>
            <h3 style={styles.sectionTitle}>App Theme Management</h3>
            
            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>App Theme Status:</div>
              <div style={styles.fieldValue}>
                <div style={styles.readOnlyValue}>
                  {currentBusiness.theme_uploaded ? (
                    <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                      ✓ App theme uploaded
                    </span>
                  ) : (
                    <span style={{ color: "#f57c00", fontWeight: "500" }}>
                      ✗ No App theme uploaded
                    </span>
                  )}
                </div>
              </div>
            </div>

            { !themeUpload.show ? (
              <CustomButton
                title={currentBusiness.theme_uploaded ? "Upload New App Theme" : "Upload App Theme"}
                name="uploadTheme"
                handleClick={() => setThemeUpload(prev => ({ ...prev, show: true }))}
                variant="secondary" // Secondary to distinguish from new dashboard theme
                loading={assetUploading && themeUpload.uploading}
              />
            ) : (
              <div style={styles.uploadSection}>
                <h4 style={{ marginBottom: "12px", fontSize: "16px" }}>
                  Upload App Theme Configuration (JSON)
                </h4>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
                  App theme for external server. For new dashboard theme, use the section below.
                </p>
                
                <textarea
                  style={styles.textarea}
                  value={themeUpload.json}
                  onChange={(e) => setThemeUpload(prev => ({ ...prev, json: e.target.value }))}
                  placeholder={`{
                      "colors": {
                        "primary": "#4F46E5",
                        "secondary": "#10B981"
                      }
                    }`}
                />
                
                <div style={styles.uploadButtonContainer}>
                  <CustomButton
                    title={assetUploading ? "Uploading..." : "Upload App Theme"}
                    name="uploadThemeAction"
                    handleClick={handleUploadTheme}
                    loading={uploading}
                    disabled={!themeUpload.json || uploading}
                    variant="secondary"
                  />
                  <CustomButton
                    title="Cancel"
                    name="cancelThemeUpload"
                    handleClick={() => setThemeUpload({
                      show: false,
                      json: "",
                      uploading: false,
                    })}
                    variant="secondary"
                    disabled={assetUploading}
                    loading={false}
                  />
                </div>
              </div>
            )}
          </div>
        
          <div style={styles.infoCard}>
  <h3 style={styles.sectionTitle}>App Assets Management</h3>
  
  {/* App Logos */}
  <div style={{ marginBottom: '30px' }}>
    <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>App Logos</h4>
    
    {/* Light Logo Status - SIMPLE STATUS */}
    <div style={styles.fieldRow}>
      <div style={styles.fieldLabel}>Light Logo Status:</div>
      <div style={styles.fieldValue}>
        <div style={styles.readOnlyValue}>
          {currentBusiness?.app_logo_light_uploaded ? (
            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
              ✓ Light logo uploaded
            </span>
          ) : (
            <span style={{ color: "#f57c00", fontWeight: "500" }}>
              ✗ No light logo uploaded
            </span>
          )}
        </div>
        {currentBusiness?.app_logo_light_url && (
          <div style={styles.assetUrl}>
            <a href={currentBusiness.app_logo_light_url} target="_blank" rel="noopener noreferrer">
              View Light Logo
            </a>
          </div>
        )}
      </div>
    </div>
    
    {/* Light Logo Upload */}
    {!appLogoLight.show ? (
      <CustomButton
        title="Upload Light Logo"
        name="uploadAppLogoLight"
        handleClick={() => setAppLogoLight(prev => ({ ...prev, show: true }))}
        variant="secondary"
        loading={appLogoLight.uploading}
       // style={{ marginTop: '10px' }}
      />
    ) : (
      <div style={styles.uploadSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h5 style={{ margin: 0 }}>Light Logo</h5>
          <span style={{ fontSize: '12px', color: '#666' }}>Will be saved as: logo-light.*</span>
        </div>
        <input
          type="file"
          accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
          onChange={(e) => handleAppLogoSelect(e.target.files?.[0] || null, 'light', setAppLogoLight)}
          style={styles.fileInput}
        />
        {appLogoLight.preview && (
          <div style={styles.previewContainer}>
            <img src={appLogoLight.preview} alt="Light logo preview" style={{ maxWidth: '100%', maxHeight: '80px' }} />
          </div>
        )}
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={appLogoLight.uploading ? "Uploading..." : "Upload Light Logo"}
            name="uploadAppLogoLightAction"
            handleClick={() => handleUploadAppLogo('light', appLogoLight, setAppLogoLight)}
            loading={appLogoLight.uploading}
            disabled={!appLogoLight.file || appLogoLight.uploading}
          />
          <CustomButton
            title="Cancel"
            name="cancelAppLogoLight"
            handleClick={() => setAppLogoLight({ show: false, file: null, fileName: "logo-light.svg", preview: null, uploading: false })}
            variant="secondary"
            loading={false}
          />
        </div>
      </div>
    )}

    {/* Dark Logo Status - SIMPLE STATUS */}
    <div style={styles.fieldRow}>
      <div style={styles.fieldLabel}>Dark Logo Status:</div>
      <div style={styles.fieldValue}>
        <div style={styles.readOnlyValue}>
          {currentBusiness?.app_logo_dark_uploaded ? (
            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
              ✓ Dark logo uploaded
            </span>
          ) : (
            <span style={{ color: "#f57c00", fontWeight: "500" }}>
              ✗ No dark logo uploaded
            </span>
          )}
        </div>
        {currentBusiness?.app_logo_dark_url && (
          <div style={styles.assetUrl}>
            <a href={currentBusiness.app_logo_dark_url} target="_blank" rel="noopener noreferrer">
              View Dark Logo
            </a>
          </div>
        )}
      </div>
    </div>
    
    {/* Dark Logo Upload */}
    {!appLogoDark.show ? (
      <CustomButton
        title="Upload Dark Logo"
        name="uploadAppLogoDark"
        handleClick={() => setAppLogoDark(prev => ({ ...prev, show: true }))}
        variant="secondary"
        loading={appLogoDark.uploading}
       // style={{ marginTop: '10px' }}
      />
    ) : (
      <div style={styles.uploadSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h5 style={{ margin: 0 }}>Dark Logo</h5>
          <span style={{ fontSize: '12px', color: '#666' }}>Will be saved as: logo-dark.*</span>
        </div>
        <input
          type="file"
          accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
          onChange={(e) => handleAppLogoSelect(e.target.files?.[0] || null, 'dark', setAppLogoDark)}
          style={styles.fileInput}
        />
        {appLogoDark.preview && (
          <div style={styles.previewContainer}>
            <img src={appLogoDark.preview} alt="Dark logo preview" style={{ maxWidth: '100%', maxHeight: '80px' }} />
          </div>
        )}
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={appLogoDark.uploading ? "Uploading..." : "Upload Dark Logo"}
            name="uploadAppLogoDarkAction"
            handleClick={() => handleUploadAppLogo('dark', appLogoDark, setAppLogoDark)}
            loading={appLogoDark.uploading}
            disabled={!appLogoDark.file || appLogoDark.uploading}
          />
          <CustomButton
            title="Cancel"
            name="cancelAppLogoDark"
            handleClick={() => setAppLogoDark({ show: false, file: null, fileName: "logo-dark.svg", preview: null, uploading: false })}
            variant="secondary"
            loading={false}
          />
        </div>
      </div>
    )}
  </div>

  {/* App Backgrounds */}
  <div style={{ marginBottom: '30px' }}>
    <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>App Backgrounds</h4>
    
    {/* Light Background Status - SIMPLE STATUS */}
    <div style={styles.fieldRow}>
      <div style={styles.fieldLabel}>Light Background Status:</div>
      <div style={styles.fieldValue}>
        <div style={styles.readOnlyValue}>
          {currentBusiness?.app_background_light_uploaded ? (
            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
              ✓ Light background uploaded
            </span>
          ) : (
            <span style={{ color: "#f57c00", fontWeight: "500" }}>
              ✗ No light background uploaded
            </span>
          )}
        </div>
        {currentBusiness?.app_background_light_url && (
          <div style={styles.assetUrl}>
            <a href={currentBusiness.app_background_light_url} target="_blank" rel="noopener noreferrer">
              View Light Background
            </a>
          </div>
        )}
      </div>
    </div>
    
    {/* Light Background Upload */}
    {!appBackgroundLight.show ? (
      <CustomButton
        title="Upload Light Background"
        name="uploadAppBgLight"
        handleClick={() => setAppBackgroundLight(prev => ({ ...prev, show: true }))}
        variant="secondary"
        loading={appBackgroundLight.uploading}
       // style={{ marginTop: '10px' }}
      />
    ) : (
      <div style={styles.uploadSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h5 style={{ margin: 0 }}>Light Background</h5>
          <span style={{ fontSize: '12px', color: '#666' }}>Will be saved as: background-light.*</span>
        </div>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          onChange={(e) => handleAppBackgroundSelect(e.target.files?.[0] || null, 'light', setAppBackgroundLight)}
          style={styles.fileInput}
        />
        {appBackgroundLight.preview && (
          <div style={styles.previewContainer}>
            <img src={appBackgroundLight.preview} alt="Light background preview" style={{ maxWidth: '100%', maxHeight: '80px' }} />
          </div>
        )}
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={appBackgroundLight.uploading ? "Uploading..." : "Upload Light Background"}
            name="uploadAppBgLightAction"
            handleClick={() => handleUploadAppBackground('light', appBackgroundLight, setAppBackgroundLight)}
            loading={appBackgroundLight.uploading}
            disabled={!appBackgroundLight.file || appBackgroundLight.uploading}
          />
          <CustomButton
            title="Cancel"
            name="cancelAppBgLight"
            handleClick={() => setAppBackgroundLight({ show: false, file: null, fileName: "background-light.jpg", preview: null, uploading: false })}
            variant="secondary"
            loading={false}
          />
        </div>
      </div>
    )}

    {/* Dark Background Status - SIMPLE STATUS */}
    <div style={styles.fieldRow}>
      <div style={styles.fieldLabel}>Dark Background Status:</div>
      <div style={styles.fieldValue}>
        <div style={styles.readOnlyValue}>
          {currentBusiness?.app_background_dark_uploaded ? (
            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
              ✓ Dark background uploaded
            </span>
          ) : (
            <span style={{ color: "#f57c00", fontWeight: "500" }}>
              ✗ No dark background uploaded
            </span>
          )}
        </div>
        {currentBusiness?.app_background_dark_url && (
          <div style={styles.assetUrl}>
            <a href={currentBusiness.app_background_dark_url} target="_blank" rel="noopener noreferrer">
              View Dark Background
            </a>
          </div>
        )}
      </div>
    </div>
    
    {/* Dark Background Upload */}
    {!appBackgroundDark.show ? (
      <CustomButton
        title="Upload Dark Background"
        name="uploadAppBgDark"
        handleClick={() => setAppBackgroundDark(prev => ({ ...prev, show: true }))}
        variant="secondary"
        loading={appBackgroundDark.uploading}
        //style={{ marginTop: '10px' }}
      />
    ) : (
      <div style={styles.uploadSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h5 style={{ margin: 0 }}>Dark Background</h5>
          <span style={{ fontSize: '12px', color: '#666' }}>Will be saved as: background-dark.*</span>
        </div>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          onChange={(e) => handleAppBackgroundSelect(e.target.files?.[0] || null, 'dark', setAppBackgroundDark)}
          style={styles.fileInput}
        />
        {appBackgroundDark.preview && (
          <div style={styles.previewContainer}>
            <img src={appBackgroundDark.preview} alt="Dark background preview" style={{ maxWidth: '100%', maxHeight: '80px' }} />
          </div>
        )}
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={appBackgroundDark.uploading ? "Uploading..." : "Upload Dark Background"}
            name="uploadAppBgDarkAction"
            handleClick={() => handleUploadAppBackground('dark', appBackgroundDark, setAppBackgroundDark)}
            loading={appBackgroundDark.uploading}
            disabled={!appBackgroundDark.file || appBackgroundDark.uploading}
          />
          <CustomButton
            title="Cancel"
            name="cancelAppBgDark"
            handleClick={() => setAppBackgroundDark({ show: false, file: null, fileName: "background-dark.jpg", preview: null, uploading: false })}
            variant="secondary"
            loading={false}
          />
        </div>
      </div>
    )}
  </div>

  {/* Onboarding Images */}
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
      <h4 style={{ margin: 0, fontSize: '16px' }}>Onboarding Images</h4>
      <button
        title={'+'}
        name="addOnboarding"
        onClick={addOnboardingImage}
        style={{ padding: '8px 12px', minWidth: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
      ><PlusCircle /></button>
    </div>

    {/* Onboarding Images Status - SIMPLE STATUS for each existing image */}
    {currentBusiness?.onboarding_images && currentBusiness.onboarding_images.length > 0 && (
      <div style={{ marginBottom: '20px' }}>
        {currentBusiness.onboarding_images.map((img, idx) => (
          <div key={img.id} style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Onboarding Image {idx + 1}:</div>
            <div style={styles.fieldValue}>
              <div style={styles.readOnlyValue}>
                <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                  ✓ Uploaded
                </span>
              </div>
              <div style={styles.assetUrl}>
                <a href={img.url} target="_blank" rel="noopener noreferrer">
                  {img.filename}
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    {onboardingImages.map((item, index) => (
      <div key={item.id} style={styles.uploadSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h5 style={{ margin: 0 }}>Onboarding Image {index + 1}</h5>
          <div>
            <span style={{ fontSize: '12px', color: '#666', marginRight: '10px' }}>
              Will be saved as: onboarding-{item.id}.*
            </span>
            <button
              title={''}
              name={`removeOnboarding${index}`}
              onClick={() => removeOnboardingImage(index)}
              style={{ padding: '4px 8px', minWidth: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
            ><MinusCircle size={16} /></button>
          </div>
        </div>
        
        <input
          type="file"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          onChange={(e) => handleOnboardingSelect(e.target.files?.[0] || null, index)}
          style={styles.fileInput}
        />
        
        {item.preview && (
          <div style={styles.previewContainer}>
            <img src={item.preview} alt={`Onboarding ${index + 1} preview`} style={{ maxWidth: '100%', maxHeight: '80px' }} />
          </div>
        )}
        
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={item.uploading ? "Uploading..." : `Upload Onboarding ${index + 1}`}
            name={`uploadOnboarding${index}`}
            handleClick={() => handleUploadOnboarding(index, item)}
            loading={item.uploading}
            disabled={!item.file || item.uploading}
            variant="primary"
          />
        </div>
      </div>
    ))}
  </div>
          </div>
            
        </>)}

        {/* Main Info Card */}
        <div style={styles.infoCard}>
          <h3 style={styles.sectionTitle}>Business Information</h3>

          {/* Business Code (Read-only) */}
          <div style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Business Code:</div>
            <div style={styles.fieldValue}>
              <div style={styles.codeDisplay}>
                {currentBusiness.code || "N/A"}
              </div>
              <small style={{ color: "#666", fontSize: "12px" }}>
                This field cannot be modified
              </small>
            </div>
          </div>

          {/* ... (keep all your existing business info fields) */}
          {/* Name */}
          <div style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Name:</div>
            <div style={styles.fieldValue}>
              {isEditing ? (
                <CustomInput
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlure={formik.handleBlur}
                  error={formik.errors.name && formik.touched.name ? formik.errors.name : ""}
                />
              ) : (
                <div style={styles.readOnlyValue}>{currentBusiness.name}</div>
              )}
            </div>
          </div>

          {/* Display Name */}
          <div style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Display Name:</div>
            <div style={styles.fieldValue}>
              {isEditing ? (
                <CustomInput
                  name="display_name"
                  value={formik.values.display_name}
                  onChange={formik.handleChange}
                  onBlure={formik.handleBlur}
                  error={formik.errors.display_name && formik.touched.display_name ? formik.errors.display_name : ""}
                />
              ) : (
                <div style={styles.readOnlyValue}>
                  {currentBusiness.display_name || "N/A"}
                </div>
              )}
            </div>
          </div>

          {/* Type */}
          <div style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Type:</div>
            <div style={styles.fieldValue}>
              {isEditing ? (
                <CustomSelect
                  name="type"
                  value={formik.values.type}
                  options={["partner", "distributer"]}
                  onChange={(e) => formik.setFieldValue("type", e.target.value)}
                  error={formik.errors.type && formik.touched.type ? formik.errors.type : ""}
                />
              ) : (
                <div style={styles.readOnlyValue}>
                  {currentBusiness.type?.charAt(0).toUpperCase() + currentBusiness.type?.slice(1)}
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div style={styles.fieldRow}>
            <div style={styles.fieldLabel}>Status:</div>
            <div style={styles.fieldValue}>
              {isEditing ? (
                <CustomSelect
                  name="status"
                  value={formik.values.status}
                  options={["active", "inactive"]}
                  onChange={(e) => formik.setFieldValue("status", e.target.value)}
                  error={formik.errors.status && formik.touched.status ? formik.errors.status : ""}
                />
              ) : (
                <div style={styles.readOnlyValue}>{currentBusiness.status}</div>
              )}
            </div>
          </div>

          {/* Partner-specific fields */}
          {isPartner && (
            <>
              {/* Enable Advisors */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Enable Advisors:</div>
                <div style={styles.fieldValue}>
                  {isEditing ? (
                    <ActiveInActiveButton
                      mainText=""
                      selected={formik.values.enable_advisors}
                      onPress={() => formik.setFieldValue("enable_advisors", !formik.values.enable_advisors)}
                      isWithWhiteBackground={false}
                    />
                  ) : (
                    <div style={styles.readOnlyValue}>
                      {currentBusiness.enable_advisors ? "Yes" : "No"}
                    </div>
                  )}
                </div>
              </div>

              {/* Need Client ID */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Need Client ID:</div>
                <div style={styles.fieldValue}>
                  {isEditing ? (
                    <ActiveInActiveButton
                      mainText=""
                      selected={formik.values.need_client_id}
                      onPress={() => formik.setFieldValue("need_client_id", !formik.values.need_client_id)}
                      isWithWhiteBackground={false}
                    />
                  ) : (
                    <div style={styles.readOnlyValue}>
                      {currentBusiness.need_client_id ? "Yes" : "No"}
                    </div>
                  )}
                </div>
              </div>

              {/* Need Admin Dashboard */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Need Admin Dashboard:</div>
                <div style={styles.fieldValue}>
                  {isEditing ? (
                    <ActiveInActiveButton
                      mainText=""
                      selected={formik.values.need_admin_dashboard}
                      onPress={() => formik.setFieldValue("need_admin_dashboard", !formik.values.need_admin_dashboard)}
                      isWithWhiteBackground={false}
                    />
                  ) : (
                    <div style={styles.readOnlyValue}>
                      {currentBusiness.need_admin_dashboard ? "Yes" : "No"}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Timestamps */}
          <div style={styles.timestampSection}>
            <h4 style={{ marginBottom: "12px", fontSize: "14px" }}>Timestamps</h4>
            <div style={styles.timestampRow}>
              <div style={styles.timestampLabel}>Created At:</div>
              <div>{formatDate(currentBusiness.created_at)}</div>
            </div>
            <div style={styles.timestampRow}>
              <div style={styles.timestampLabel}>Updated At:</div>
              <div>{formatDate(currentBusiness.updated_at)}</div>
            </div>
            {currentBusiness.theme_uploaded_at && (
              <div style={styles.timestampRow}>
                <div style={styles.timestampLabel}>Theme Uploaded:</div>
                <div>{formatDate(currentBusiness.theme_uploaded_at)}</div>
              </div>
            )}
            {currentBusiness.logo_uploaded_at && (
              <div style={styles.timestampRow}>
                <div style={styles.timestampLabel}>Logo Uploaded:</div>
                <div>{formatDate(currentBusiness.logo_uploaded_at)}</div>
              </div>
            )}
            {currentBusiness.background_uploaded_at && (
              <div style={styles.timestampRow}>
                <div style={styles.timestampLabel}>Background Uploaded:</div>
                <div>{formatDate(currentBusiness.background_uploaded_at)}</div>
              </div>
            )}
          </div>

          {/* Admins Section */}
          {currentBusiness.need_admin_dashboard && adminsList.length > 0 && (
            <div style={styles.adminsSection}>
              <h4 style={{ marginBottom: "12px", fontSize: "14px" }}>Admin Users</h4>
              {adminsList.map((admin) => (
                <div key={admin.id} style={styles.adminCard}>
                  <div style={styles.adminEmail}>{admin.email}</div>
                  <div style={styles.adminRoles}>
                    Roles: {admin.roles.join(", ")}
                  </div>
                  {admin.created_at && (
                    <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                      Created: {formatDate(admin.created_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

                  {/* Logo Upload Section - Only for partners with admin dashboard */}
        {isEditing && isPartner && formik.values.need_admin_dashboard && (
            <>
          <div style={styles.infoCard}>
            <h3 style={styles.sectionTitle}> Dshboard Logos Management</h3>
            
            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Logo Status:</div>
              <div style={styles.fieldValue}>
                <div style={styles.readOnlyValue}>
                  {currentBusiness.logo_uploaded ? (
                    <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                      ✓ Logo uploaded
                    </span>
                  ) : (
                    <span style={{ color: "#f57c00", fontWeight: "500" }}>
                      ✗ No logo uploaded
                    </span>
                  )}
                </div>
                {currentBusiness.logo_url && (
                  <div style={styles.assetUrl}>
                    <a href={currentBusiness.logo_url} target="_blank" rel="noopener noreferrer">
                      View Current Logo
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* ... (keep your existing logo upload UI) */}
            {!logoUpload.show ? (
              <CustomButton
                title={currentBusiness.logo_uploaded ? "Upload New Logo" : "Upload Logo"}
                name="uploadLogo"
                handleClick={() => setLogoUpload(prev => ({ ...prev, show: true }))}
                variant="primary"
                loading={assetUploading && logoUpload.uploading}
              />
            ) : (
              <div style={styles.uploadSection}>
                <h4 style={{ marginBottom: "12px", fontSize: "16px" }}>
                  Upload Logo (SVG/PNG/JPG)
                </h4>
                
                <input
                  type="file"
                  accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                  onChange={handleLogoSelect}
                  style={styles.fileInput}
                />
                
                <input
                  type="text"
                  style={styles.fileNameInput}
                  value={logoUpload.fileName}
                  onChange={(e) => setLogoUpload(prev => ({ ...prev, fileName: e.target.value }))}
                  placeholder="primary_logo_light.svg"
                />
                
                {logoUpload.preview && (
                  <div style={styles.previewContainer}>
                    <img 
                      src={logoUpload.preview} 
                      alt="Logo preview" 
                      style={{ maxWidth: "100%", maxHeight: "80px" }} 
                    />
                  </div>
                )}
                
                <div style={styles.uploadButtonContainer}>
                  <CustomButton
                    title={assetUploading ? "Uploading..." : "Upload Logo"}
                    name="uploadLogoAction"
                    handleClick={handleUploadLogo}
                    loading={assetUploading && logoUpload.uploading}
                    disabled={!logoUpload.file || !logoUpload.fileName || assetUploading}
                  />
                  <CustomButton
                    title="Cancel"
                    name="cancelLogoUpload"
                    handleClick={() => setLogoUpload({
                      show: false,
                      file: null,
                      fileName: "primary_logo_light.svg",
                      preview: null,
                      uploading: false,
                    })}
                    variant="secondary"
                    disabled={assetUploading}
                    loading={false}
                  />
                </div>
              </div>
            )}
          </div>
    
        
          <div style={styles.infoCard}>
            <h3 style={styles.sectionTitle}>Dashboard Backgrounds Management</h3>
            
            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Background Status:</div>
              <div style={styles.fieldValue}>
                <div style={styles.readOnlyValue}>
                  {currentBusiness.background_uploaded ? (
                    <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                      ✓ Background uploaded
                    </span>
                  ) : (
                    <span style={{ color: "#f57c00", fontWeight: "500" }}>
                      ✗ No background uploaded
                    </span>
                  )}
                </div>
                {currentBusiness.background_url && (
                  <div style={styles.assetUrl}>
                    <a href={currentBusiness.background_url} target="_blank" rel="noopener noreferrer">
                      View Current Background
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* ... (keep your existing background upload UI) */}
            {!backgroundUpload.show ? (
              <CustomButton
                title={currentBusiness.background_uploaded ? "Upload New Background" : "Upload Background"}
                name="uploadBackground"
                handleClick={() => setBackgroundUpload(prev => ({ ...prev, show: true }))}
                variant="primary"
                loading={assetUploading && backgroundUpload.uploading}
              />
            ) : (
              <div style={styles.uploadSection}>
                <h4 style={{ marginBottom: "12px", fontSize: "16px" }}>
                  Upload Background (JPG/PNG/SVG)
                </h4>
                
                <input
                  type="file"
                  accept=".svg,.jpg,.jpeg,.png,image/jpeg,image/png"
                  onChange={handleBackgroundSelect}
                  style={styles.fileInput}
                />
                
                <input
                  type="text"
                  style={styles.fileNameInput}
                  value={backgroundUpload.fileName}
                  onChange={(e) => setBackgroundUpload(prev => ({ ...prev, fileName: e.target.value }))}
                  placeholder="dashboard_background.jpg"
                />
                
                {backgroundUpload.preview && (
                  <div style={styles.previewContainer}>
                    <img 
                      src={backgroundUpload.preview} 
                      alt="Background preview" 
                      style={{ maxWidth: "100%", maxHeight: "80px" }} 
                    />
                  </div>
                )}
                
                <div style={styles.uploadButtonContainer}>
                  <CustomButton
                    title={assetUploading ? "Uploading..." : "Upload Background"}
                    name="uploadBackgroundAction"
                    handleClick={handleUploadBackground}
                    loading={assetUploading && backgroundUpload.uploading}
                    disabled={!backgroundUpload.file || !backgroundUpload.fileName || assetUploading}
                  />
                  <CustomButton
                    title="Cancel"
                    name="cancelBackgroundUpload"
                    handleClick={() => setBackgroundUpload({
                      show: false,
                      file: null,
                      fileName: "dashboard_background.jpg",
                      preview: null,
                      uploading: false,
                    })}
                    variant="secondary"
                    disabled={assetUploading}
                    loading={false}
                  />
                </div>
              </div>
            )}
          </div>
        

          {/* Dashboard Theme Upload Section - Only for partners with admin dashboard */}
{isPartner && currentBusiness.need_admin_dashboard && (
  <div style={styles.infoCard}>
    <h3 style={styles.sectionTitle}>
      Dashboard Theme Management 
      {currentBusiness.theme_version && (
        <span style={styles.versionBadge}>v{currentBusiness.theme_version}</span>
      )}
    </h3>
    
    {/* <div style={styles.fieldRow}>
      <div style={styles.fieldLabel}>Dashboard Theme Status:</div>
      <div style={styles.fieldValue}>
        <div style={styles.readOnlyValue}>
          {currentBusiness.theme_version ? (
            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
              ✓ Dashboard theme v{currentBusiness.theme_version} uploaded
            </span>
          ) : (
            <span style={{ color: "#f57c00", fontWeight: "500" }}>
              ✗ No dashboard theme uploaded
            </span>
          )}
        </div>
      </div>
    </div> */}

    {!dashboardThemeUpload.show ? (
      <CustomButton
        title={currentBusiness.theme_version ? "Upload New Dashboard Theme" : "Upload Dashboard Theme"}
        name="uploadDashboardTheme"
        handleClick={() => setDashboardThemeUpload(prev => ({ ...prev, show: true }))}
        variant="primary"
        loading={dashboardThemeUploading}
      />
    ) : (
      <div style={styles.uploadSection}>
        <h4 style={{ marginBottom: "12px", fontSize: "16px" }}>
          Upload Dashboard Theme Configuration (JavaScript Object)
        </h4>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
          Paste a JavaScript object below. It will be automatically parsed to JSON.
          {currentBusiness.theme_version && (
            <strong> Current version: v{currentBusiness.theme_version}</strong>
          )}
        </p>
        
        <textarea
          style={styles.textarea}
          value={dashboardThemeUpload.json}
        //   defaultValue={ businessThemeRaw? `${themeToJSObjectString(businessThemeRaw)}` : `{
        //     name: "Dashboard Theme Name",
        //     description: "Optional description",
        //     colors: {
        //         primary: "#4F46E5",
        //         secondary: "#10B981", 
        //         accent: "#F59E0B",
        //         background: "#FFFFFF",
        //         text: "#111827"
        //     },
        //     fonts: {
        //         primary: "Inter, sans-serif",
        //         secondary: "Roboto, sans-serif"
        //     },
        //     borderRadius: "8px",
        //     spacing: "4px",
        //     logoUrl: "https://your-cdn.com/logo.svg"
        //     }`}
          onChange={(e) => setDashboardThemeUpload(prev => ({ ...prev, json: e.target.value }))}
        //   placeholder={ businessThemeRaw? `${themeToJSObjectString(businessThemeRaw)}` : `{
        //         name: "Dashboard Theme Name",
        //         description: "Optional description",
        //         colors: {
        //             primary: "#4F46E5",
        //             secondary: "#10B981", 
        //             accent: "#F59E0B",
        //             background: "#FFFFFF",
        //             text: "#111827"
        //         },
        //         fonts: {
        //             primary: "Inter, sans-serif",
        //             secondary: "Roboto, sans-serif"
        //         },
        //         borderRadius: "8px",
        //         spacing: "4px",
        //         logoUrl: "https://your-cdn.com/logo.svg"
        //         }`}
        />
        
        
        <div style={styles.uploadButtonContainer}>
          <CustomButton
            title={dashboardThemeUploading ? "Uploading..." : "Upload Dashboard Theme"}
            name="uploadDashboardThemeAction"
            handleClick={handleUploadDashboardTheme}
            loading={dashboardThemeUploading}
            disabled={!dashboardThemeUpload.json || dashboardThemeUploading}
            variant="primary"
          />
          <CustomButton
            title="Cancel"
            name="cancelDashboardThemeUpload"
            handleClick={() => setDashboardThemeUpload({
              show: false,
              json: "",
              uploading: false,
            })}
            variant="secondary"
            disabled={dashboardThemeUploading}
            loading={false}
          />
        </div>
        
        {dashboardThemeUpload.json && (
          <div style={{ marginTop: "10px", fontSize: "12px" }}>
            {(() => {
              try {
                eval('(' + dashboardThemeUpload.json + ')');
                return <span style={{ color: "#28a745" }}>✅ Valid JavaScript object - ready to upload</span>;
              } catch (e) {
                return <span style={{ color: "#dc3545" }}>❌ Invalid JavaScript object</span>;
              }
            })()}
          </div>
        )}
      </div>
    )}
  </div>
)}
       </> )}

          {/* Edit Mode Actions */}
          {isEditing && (
            <div style={styles.buttonContainer}>
              <CustomButton
                title="Save Changes"
                name="saveChanges"
                handleClick={() => formik.handleSubmit()}
                loading={updating}
                disabled={!formik.isValid || updating || Object.keys(formik.touched).length === 0}
              />
              <CustomButton
                title="Cancel"
                name="cancelEdit"
                handleClick={toggleEdit}
                variant="secondary"
                disabled={updating}
                loading={false}
              />
            </div>
          )}
        </div>

          


      </div>
    </div>
  );
};

export default BusinessDetails;